import { Component, type ErrorInfo, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sentry } from "@/lib/sentry";

const RELOAD_ONCE_KEY = "sozu-admin:auto-reload-once";

interface Props {
  children: ReactNode;
}

interface Estado {
  error: Error | null;
}

/**
 * Red de seguridad global del panel. Antes de esto la app no tenía ningún
 * `ErrorBoundary` en la raíz: una excepción de render (por ejemplo un chunk de
 * ruta que ya no existe tras un deploy, ver `lazyRetry` en `src/lib/lazyRetry.ts`) desmontaba
 * el árbol completo y dejaba la pantalla colgada en el loader estático de
 * `index.html`, sin ninguna pista de qué pasó — el usuario tenía que saber que
 * debía presionar F5.
 *
 * Aquí se intenta UN solo auto-reload por pestaña (mismo patrón que
 * `lazyRetry`: bandera en `sessionStorage`, se limpia sola al cerrar la
 * pestaña). Si el error persiste después de ese reload, ya no es probable que
 * sea un deploy desfasado — se muestra el mensaje en vez de recargar en bucle.
 */
export class ErrorBoundaryGlobal extends Component<Props, Estado> {
  state: Estado = { error: null };

  static getDerivedStateFromError(error: Error): Partial<Estado> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[AdminPanel] error no controlado en la raíz:", error, info.componentStack);
    // Antes del auto-reload: si no, el error que tumbó la página nunca llega a
    // Sentry. Sin DSN no hace nada.
    Sentry.captureException(error, { contexts: { react: { componentStack: info.componentStack } } });
    if (!sessionStorage.getItem(RELOAD_ONCE_KEY)) {
      sessionStorage.setItem(RELOAD_ONCE_KEY, "1");
      window.location.reload();
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    // Ya se disparó un auto-reload para este error (ver componentDidCatch,
    // corre después de este render). Mientras tanto, mostrar un loader en vez
    // de un mensaje: en la mayoría de los casos el reload resuelve el problema
    // y el usuario nunca llega a ver el mensaje de error.
    if (!sessionStorage.getItem(RELOAD_ONCE_KEY)) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-8">
        <div className="max-w-md space-y-4 text-center">
          <h1 className="text-xl font-bold text-destructive">No se pudo cargar la página</h1>
          <p className="text-sm text-muted-foreground">
            Puede haber una versión nueva del panel. Recarga para intentar de nuevo; si el
            problema sigue, contacta a soporte.
          </p>
          {/* En local el error real ahorra abrir la consola; en producción no se expone. */}
          {import.meta.env.DEV && (
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-left text-xs text-muted-foreground">
              {this.state.error.message}
            </pre>
          )}
          <Button
            onClick={() => {
              sessionStorage.removeItem(RELOAD_ONCE_KEY);
              window.location.reload();
            }}
          >
            Recargar página
          </Button>
        </div>
      </div>
    );
  }
}
